import type { OnboardingProfile, OnboardingSessionPlan } from './onboarding';

/**
 * localStorage fallback for the team / sessions Onboarding tries to write.
 * When the real Supabase is unreachable, the coach can still complete the
 * Layer 1 flow and have their team appear across the rest of the app.
 *
 * This is purely a development convenience — production paths still use
 * Supabase. Once a real DB write succeeds, the localStorage entry can be
 * cleared.
 */

const TEAM_KEY = '4cm_local_team';
const SESSIONS_KEY = '4cm_local_sessions';

export interface LocalTeam {
  id: string;
  name: string;
  age_group: string;
  player_count: number;
  coach_level: string;
  focus: string;
  methodology: string | null;
  formation: string | null;
  training_days_per_week: number | null;
  session_duration_mins: number | null;
  created_at: string;
}

export interface LocalSession {
  id: string;
  team_id: string;
  date: string;
  title: string;
  objectives: string[];
  phase_coverage: Record<string, number>;
  psych_social_intent: string;
  duration_mins: number | null;
}

export function saveLocalTeam(profile: OnboardingProfile, sessions: OnboardingSessionPlan[]): LocalTeam {
  const teamId = `local-${crypto.randomUUID()}`;
  const team: LocalTeam = {
    id: teamId,
    name: profile.teamName ?? `Aurora FC ${profile.ageGroup ?? 'U12'}`,
    age_group: profile.ageGroup ?? 'U12',
    player_count: profile.playerCount ?? 12,
    coach_level: profile.coachLevel,
    focus: profile.focus,
    methodology: profile.methodology,
    formation: profile.formation,
    training_days_per_week: profile.trainingDaysPerWeek,
    session_duration_mins: profile.sessionDurationMins,
    created_at: new Date().toISOString(),
  };
  const sessionRows: LocalSession[] = sessions.map((s, i) => ({
    id: `local-${teamId}-${i}`,
    team_id: teamId,
    date: s.date,
    title: s.title,
    objectives: s.objectives,
    phase_coverage: { ...s.phaseCoverage } as Record<string, number>,
    psych_social_intent: s.psychSocialIntent,
    duration_mins: profile.sessionDurationMins,
  }));
  try {
    localStorage.setItem(TEAM_KEY, JSON.stringify(team));
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessionRows));
  } catch (err) {
    console.warn('[local team] could not persist', err);
  }
  return team;
}

export function loadLocalTeam(): LocalTeam | null {
  try {
    const raw = localStorage.getItem(TEAM_KEY);
    return raw ? (JSON.parse(raw) as LocalTeam) : null;
  } catch { return null; }
}

export function loadLocalSessions(): LocalSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    return raw ? (JSON.parse(raw) as LocalSession[]) : [];
  } catch { return []; }
}

export function clearLocalTeam(): void {
  try {
    localStorage.removeItem(TEAM_KEY);
    localStorage.removeItem(SESSIONS_KEY);
  } catch { /* noop */ }
}

/**
 * Persist a Brain-drafted session into the same local sessions store
 * that onboarding's Week 1 sessions land in. Used by SessionNew when
 * Supabase is the placeholder env — without this the draft would be
 * lost to a network error on commit.
 *
 * The payload shape comes from the Brain draft (see `@4cm/brain`); we
 * read the fields we know are present and tolerate the rest being
 * absent. Returned id is "local-draft-<timestamp>" so it lives in the
 * same UUID-prefixed namespace as onboarding sessions and SessionDetail
 * can resolve it via loadLocalSessions().
 */
export function saveLocalDraftSession(teamId: string, payload: unknown): LocalSession {
  const id = `local-draft-${Date.now()}`;
  const obj = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const dateValue = typeof obj.date === 'string' ? obj.date : new Date().toISOString();
  const titleValue = typeof obj.title === 'string' ? obj.title : 'Brain-drafted session';
  const objectives = Array.isArray(obj.objectives)
    ? (obj.objectives as unknown[]).filter((o): o is string => typeof o === 'string')
    : [];
  const phaseCoverage = (obj.phase_coverage && typeof obj.phase_coverage === 'object')
    ? (obj.phase_coverage as Record<string, number>)
    : { attacking: 25, defending: 25, transitions: 25, individual: 25 };
  const intent = typeof obj.psych_social_intent === 'string'
    ? obj.psych_social_intent
    : '';
  const duration = typeof obj.duration_mins === 'number' ? obj.duration_mins : null;

  const session: LocalSession = {
    id,
    team_id: teamId,
    date: dateValue,
    title: titleValue,
    objectives,
    phase_coverage: phaseCoverage,
    psych_social_intent: intent,
    duration_mins: duration,
  };

  try {
    const existing = loadLocalSessions();
    localStorage.setItem(SESSIONS_KEY, JSON.stringify([...existing, session]));
  } catch (err) {
    console.warn('[local team] could not persist draft session', err);
    throw new Error('Could not save draft locally — storage unavailable.');
  }
  return session;
}
